
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClientStatus } from "@/types/clientStatus";
import { useCreateClientStatus, useUpdateClientStatus } from "@/hooks/useClientStatuses";
import { HexColorPicker } from "react-colorful";
import { Card } from "@/components/ui/card";

interface ClientStatusDialogProps {
  status?: ClientStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const ClientStatusDialog = ({ 
  status, 
  open, 
  onOpenChange, 
  onSuccess 
}: ClientStatusDialogProps) => {
  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    status_key: "",
    color: "#6366f1",
    sort_order: 1,
    is_active: true,
    show_in_top_nav: true,
  });

  const createMutation = useCreateClientStatus();
  const updateMutation = useUpdateClientStatus();

  useEffect(() => {
    if (status) {
      setFormData({
        name_ar: status.name_ar,
        name_en: status.name_en,
        status_key: status.status_key,
        color: status.color,
        sort_order: status.sort_order,
        is_active: status.is_active,
        show_in_top_nav: status.show_in_top_nav,
      });
    } else {
      setFormData({
        name_ar: "",
        name_en: "",
        status_key: "",
        color: "#6366f1",
        sort_order: 1,
        is_active: true,
        show_in_top_nav: true,
      });
    }
  }, [status, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (status) {
        await updateMutation.mutateAsync({ id: status.id, ...formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving status:", error);
    }
  };

  const generateStatusKey = (nameAr: string) => {
    return nameAr
      .replace(/\s+/g, '_')
      .replace(/[^\w\u0600-\u06FF]/g, '')
      .toLowerCase();
  };

  const handleNameArChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name_ar: value,
      status_key: prev.status_key || generateStatusKey(value)
    }));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>
            {status ? "تعديل حالة العميل" : "إضافة حالة عميل جديدة"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name_ar">الاسم بالعربية *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => handleNameArChange(e.target.value)}
                placeholder="في الانتظار"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name_en">الاسم بالإنجليزية *</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                placeholder="Waiting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status_key">مفتاح الحالة *</Label>
              <Input
                id="status_key"
                value={formData.status_key}
                onChange={(e) => setFormData(prev => ({ ...prev, status_key: e.target.value }))}
                placeholder="waiting"
                required
              />
              <p className="text-xs text-gray-500">
                يُستخدم في النظام لربط الحالة مع الأقسام الأخرى
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sort_order">ترتيب العرض</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                min="1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <Label>لون الحالة</Label>
            <div className="flex items-center gap-4">
              <Card className="p-4">
                <HexColorPicker
                  color={formData.color}
                  onChange={(color) => setFormData(prev => ({ ...prev, color }))}
                />
              </Card>
              <div className="space-y-2">
                <div 
                  className="w-20 h-10 rounded border"
                  style={{ backgroundColor: formData.color }}
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#6366f1"
                />
                <div className="flex items-center gap-2 text-sm">
                  <span 
                    className="px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: formData.color }}
                  >
                    {formData.name_ar || "معاينة"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "جاري الحفظ..." : (status ? "تحديث" : "إضافة")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
